import * as THREE from "three";
import { PMREMGenerator } from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

/**
 * Persistent Beacons — controlled material study.
 *
 * Three variants of the SAME scene. Camera path, beacon positions,
 * duration, environment map, and light intensity are identical
 * across all three. Only the surface response differs.
 *
 * A — Smoother geometry.
 *     Higher-tessellation sphere. Silhouette reads as continuous.
 *     Any perceived structure must come from light behavior.
 *
 * B — Micro-roughness variation.
 *     Same smooth geometry as A, but a subtle procedural roughness
 *     map introduces coherence in the specular highlights. The
 *     viewer should notice strange coherence *before* understanding
 *     why.
 *
 * C — Shallow latent planes.
 *     Same smooth geometry as A, but a low-strength normal map
 *     embeds shallow planar tendencies. At a grazing angle, a
 *     "facet" becomes momentarily perceptible without ever reading
 *     as a polygon.
 *
 * Evaluation questions (see BEACONS_BRIEF.md):
 *   - In which variant did I notice the light behaving differently
 *     before I noticed the surface was different?
 *   - When the highlight passes, do I still believe the beacon was
 *     always this way?
 */

export const BEACONS_DEFAULTS = {
  beaconRadius: 0.55,
  beaconSeparation: 2.6,
  cameraStartAngle: -0.35,
  cameraEndAngle:    0.35,
  cameraRadius: 4.2,
  cameraHeight: 0.35,
  arcDuration: 22.0,
  environmentIntensity: 0.62,
  variant: "A",
};

// ---- Procedural texture generators (128x128 canvases). Kept tiny
// and cheap so nothing about the scene setup fights the render loop. ----

function makeRoughnessMap() {
  // Subtle low-frequency roughness variation across the sphere.
  // Range: 0.20–0.30 (dark = smoother, light = rougher). The
  // difference must not be visible as texture; only as coherence
  // in the highlights.
  const size = 256;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  const img = ctx.createImageData(size, size);
  // Two overlaid low-frequency sinusoids — creates smooth patches
  // of slightly-more-mirror-like / slightly-less-mirror-like.
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size, v = y / size;
      const n =
        Math.sin(u * Math.PI * 2 * 1.7 + v * Math.PI * 2 * 0.9) * 0.5 +
        Math.sin(u * Math.PI * 2 * 0.5 - v * Math.PI * 2 * 1.3) * 0.5;
      // Map [-1,1] → [0.20, 0.30]
      const r = 0.20 + (n * 0.5 + 0.5) * 0.10;
      const g = Math.round(r * 255);
      const idx = (y * size + x) * 4;
      img.data[idx] = g; img.data[idx+1] = g; img.data[idx+2] = g; img.data[idx+3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function makeLatentPlaneNormalMap() {
  // Extremely shallow normal deviations arranged in wide, plane-like
  // bands. At grazing angles the light response reads as a "facet"
  // for an instant; from other angles the surface reads as smooth.
  const size = 256;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size, v = y / size;
      // Three overlaid wide plane-like bands at different orientations.
      const b1 = Math.sin(u * Math.PI * 2 * 2.2);
      const b2 = Math.sin(v * Math.PI * 2 * 1.8);
      const b3 = Math.sin((u + v) * Math.PI * 2 * 1.5);
      const band = (b1 + b2 + b3) / 3; // range [-1, 1]
      // Convert to a very shallow normal deviation (mostly z-up).
      const strength = 0.06; // very shallow
      const nx = band * strength;
      const ny = band * strength * 0.8;
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
      const idx = (y * size + x) * 4;
      img.data[idx]   = Math.round((nx * 0.5 + 0.5) * 255);
      img.data[idx+1] = Math.round((ny * 0.5 + 0.5) * 255);
      img.data[idx+2] = Math.round((nz * 0.5 + 0.5) * 255);
      img.data[idx+3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

export function createBeaconsScene({ container, initialParams = BEACONS_DEFAULTS } = {}) {
  const params = { ...BEACONS_DEFAULTS, ...initialParams };

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setClearColor(0x000000, 1);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();

  const pmremGenerator = new PMREMGenerator(renderer);
  const envMap = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environment = envMap;

  const camera = new THREE.PerspectiveCamera(
    38,
    container.clientWidth / container.clientHeight,
    0.05,
    100
  );
  const placeCamera = (angle) => {
    camera.position.set(
      Math.sin(angle) * params.cameraRadius,
      params.cameraHeight,
      Math.cos(angle) * params.cameraRadius
    );
    camera.lookAt(0, 0, 0);
  };
  placeCamera(params.cameraStartAngle);

  // Smooth base geometry for all variants. Silhouette is continuous.
  // No polygonal tell-tales — the surface reveals through light, not
  // shape.
  const beaconGeom = new THREE.SphereGeometry(params.beaconRadius, 128, 96);

  // Optional textures — only allocated for variants that need them.
  let roughnessMap = null;
  let normalMap = null;
  if (params.variant === "B") {
    roughnessMap = makeRoughnessMap();
  } else if (params.variant === "C") {
    normalMap = makeLatentPlaneNormalMap();
  }

  const makeBeaconMaterial = () => {
    const base = {
      color: 0x1a1512,
      metalness: 0.25,
      clearcoat: 0.6,
      clearcoatRoughness: 0.15,
      envMapIntensity: params.environmentIntensity,
      emissive: 0x2a1810,
      emissiveIntensity: 0.35,
    };
    if (params.variant === "B") {
      return new THREE.MeshPhysicalMaterial({
        ...base,
        roughness: 0.24,
        roughnessMap,
      });
    }
    if (params.variant === "C") {
      return new THREE.MeshPhysicalMaterial({
        ...base,
        roughness: 0.24,
        normalMap,
        normalScale: new THREE.Vector2(0.35, 0.35),
      });
    }
    // Variant A — the control. Nothing but smoothness.
    return new THREE.MeshPhysicalMaterial({ ...base, roughness: 0.24 });
  };

  const halfSep = params.beaconSeparation * 0.5;
  const beaconA = new THREE.Mesh(beaconGeom, makeBeaconMaterial());
  beaconA.position.set(-halfSep, 0, 0);
  scene.add(beaconA);

  const beaconB = new THREE.Mesh(beaconGeom, makeBeaconMaterial());
  beaconB.position.set(halfSep, 0, 0);
  scene.add(beaconB);

  let running = true;
  let startTime = null;
  let disposed = false;

  const onResize = () => {
    if (disposed) return;
    const w = container.clientWidth;
    const h = container.clientHeight;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", onResize);

  const loop = (t) => {
    if (disposed) return;
    if (startTime === null) startTime = t;
    const elapsed = (t - startTime) / 1000;

    if (running) {
      const arcT = Math.min(1, elapsed / params.arcDuration);
      const eased = arcT < 0.5
        ? 2 * arcT * arcT
        : 1 - Math.pow(-2 * arcT + 2, 2) / 2;
      const angle = params.cameraStartAngle +
        (params.cameraEndAngle - params.cameraStartAngle) * eased;
      placeCamera(angle);
    }

    renderer.render(scene, camera);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  return {
    play() { startTime = null; running = true; },
    stop() { running = false; },
    getState() {
      const elapsed = startTime ? (performance.now() - startTime) / 1000 : 0;
      return {
        state: elapsed >= params.arcDuration ? "done" : "arcing",
        phaseT: elapsed,
        phaseDuration: params.arcDuration,
        phaseFraction: Math.min(1, elapsed / params.arcDuration),
      };
    },
    variant: params.variant,
    dispose() {
      disposed = true;
      running = false;
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      beaconGeom.dispose();
      beaconA.material.dispose();
      beaconB.material.dispose();
      if (roughnessMap) roughnessMap.dispose();
      if (normalMap) normalMap.dispose();
      envMap.dispose();
      pmremGenerator.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    },
  };
}
